import {Box, Skeleton, TableCell, TableRow, Typography} from '@mui/material';
import {alpha} from '@mui/material/styles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import React from 'react';
import {useTranslation} from 'react-i18next';

import PlayerACWRGasGauge from '../Player/components/PlayerACWRGasGauge';
import {formatTime} from '../../services/formatService';
import {PlayerMetricEntry} from '../../hooks/useTeamPlayerMetrics';
import {
  COLOR_DANGER,
  COLOR_GRAY_MEDIUM,
  COLOR_SUCCESS,
  GRAY_COLOR_5A5A5C,
  WHITE_COLOR,
} from '../../styles';

// Gauge size that keeps the row compact without the gauge overflowing the
// table (which gave the TableContainer a scrollbar).
const GAUGE_SIZE = 70;

interface Props {
  firstName: string;
  lastName: string;
  positionAbbr: string;
  number?: number;
  playerACWR?: number;
  metrics?: PlayerMetricEntry;
  metricsLoading?: boolean;
  onOpen: () => void;
}

const ChangeArrow: React.FC<{
  pct?: number;
  goodDirection?: 'up' | 'down' | null;
}> = ({pct, goodDirection = null}) => {
  if (pct == null || Math.round(pct) === 0) {
    return (
      <Typography
        variant="caption"
        sx={{color: COLOR_GRAY_MEDIUM, whiteSpace: 'nowrap'}}>
        — 0%
      </Typography>
    );
  }
  const up = pct > 0;
  let color: string = COLOR_GRAY_MEDIUM;
  if (goodDirection) {
    const good = (up ? 'up' : 'down') === goodDirection;
    color = good ? COLOR_SUCCESS : COLOR_DANGER;
  }
  return (
    <Typography variant="caption" sx={{color, whiteSpace: 'nowrap'}}>
      {up ? '▲' : '▼'} {Math.abs(Math.round(pct))}%
    </Typography>
  );
};

const MetricCell: React.FC<{
  value?: number;
  suffix?: string;
  decimals?: number;
  format?: (value: number) => string;
  showTrend?: boolean;
  loading?: boolean;
  change?: number;
  goodDirection?: 'up' | 'down' | null;
}> = ({
  value,
  suffix,
  decimals = 0,
  format,
  showTrend,
  loading,
  change,
  goodDirection,
}) => {
  const body = loading ? (
    <Skeleton
      variant="text"
      width={40}
      sx={showTrend ? {ml: 'auto'} : {mx: 'auto'}}
    />
  ) : value == null ? (
    <Typography sx={{color: COLOR_GRAY_MEDIUM, fontFamily: 'Roboto Condensed'}}>
      —
    </Typography>
  ) : (
    <Typography
      sx={{
        fontFamily: 'Roboto Condensed',
        fontWeight: 700,
        fontSize: 20,
        lineHeight: 1,
      }}>
      {format ? format(value) : value.toFixed(decimals)}
      {suffix && (
        <Typography
          component="span"
          sx={{color: COLOR_GRAY_MEDIUM, fontSize: 11, ml: '2px'}}>
          {suffix}
        </Typography>
      )}
    </Typography>
  );

  if (!showTrend) {
    return <TableCell align="center">{body}</TableCell>;
  }

  return (
    <TableCell align="center">
      <Box sx={{display: 'flex', alignItems: 'baseline'}}>
        <Box sx={{flex: 1, textAlign: 'right'}}>{body}</Box>
        <Box sx={{flex: 1, textAlign: 'left', pl: 0.75}}>
          {!loading && value != null && change !== undefined && (
            <ChangeArrow pct={change} goodDirection={goodDirection} />
          )}
        </Box>
      </Box>
    </TableCell>
  );
};

const PlayerListItem: React.FC<Props> = ({
  firstName,
  lastName,
  positionAbbr,
  number,
  playerACWR,
  metrics,
  metricsLoading,
  onOpen,
}) => {
  const {t} = useTranslation();
  const values = metrics?.values;
  const change = metrics?.change;
  // App convention: number 0 (or missing) means "no jersey number assigned".
  const hasNumber = number != null && number > 0;
  return (
    <TableRow
      onClick={onOpen}
      sx={{
        cursor: 'pointer',
        '& > td': {
          backgroundColor: alpha(WHITE_COLOR, 0.035),
          borderBottom: 'none',
          py: 0.25,
        },
        // Declared here rather than via the cell's own sx: the `& > td` rule
        // above is more specific than a bare cell class and would win.
        '& > td.gauge-cell': {py: 0},
        '& > td:first-of-type': {
          borderTopLeftRadius: 8,
          borderBottomLeftRadius: 8,
        },
        '& > td:last-of-type': {
          borderTopRightRadius: 8,
          borderBottomRightRadius: 8,
        },
        '&:hover > td': {backgroundColor: alpha(WHITE_COLOR, 0.07)},
        '&:hover .player-name': {color: 'primary.main'},
        '&:hover .row-chevron': {color: 'primary.main'},
      }}>
      <TableCell align="center" sx={{pl: 2}}>
        <Typography
          sx={{
            fontFamily: 'Roboto Condensed',
            fontWeight: 700,
            fontSize: 22,
            color: hasNumber ? 'text.secondary' : GRAY_COLOR_5A5A5C,
          }}>
          {hasNumber ? number : '#'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography
          className="player-name"
          sx={{fontStyle: 'italic', fontWeight: 700, fontSize: 19}}>
          {firstName} {lastName}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Box
          component="span"
          sx={{
            fontFamily: 'Roboto Condensed',
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: '0.5px',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '6px',
            px: 1,
            py: '3px',
            color: 'text.secondary',
          }}>
          {positionAbbr}
        </Box>
      </TableCell>
      <TableCell align="center" className="gauge-cell">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            '& svg': {display: 'block'},
          }}>
          <PlayerACWRGasGauge acwr={playerACWR || 0} size={GAUGE_SIZE} />
        </Box>
      </TableCell>
      <MetricCell
        value={values?.playerLoad}
        loading={metricsLoading}
        change={change?.playerLoad}
        goodDirection={null}
        showTrend
      />
      <MetricCell
        value={values?.energyRatio}
        suffix={t('general.units.percentSymbol') as string}
        loading={metricsLoading}
        change={change?.energyRatio}
        goodDirection="up"
        showTrend
      />
      <MetricCell
        value={values?.hustle}
        loading={metricsLoading}
        change={change?.hustle}
        goodDirection="up"
        showTrend
      />
      <MetricCell
        value={values?.timeOnIce}
        loading={metricsLoading}
        format={formatTime}
      />
      <TableCell align="center" sx={{pr: 1}}>
        <ChevronRightIcon
          className="row-chevron"
          sx={{color: GRAY_COLOR_5A5A5C}}
        />
      </TableCell>
    </TableRow>
  );
};

export default PlayerListItem;
